import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, AlertCircle, ArrowLeft, Send } from 'lucide-react';
import './GPSVibeCheckSurvey.css';

const GPSVibeCheckSurvey = () => {
  const { assignmentId, pillarNumber } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const userEmail = user?.email;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [step, setStep] = useState(1);
  const [vibeCheckAnswer, setVibeCheckAnswer] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('');
  const [actionItems, setActionItems] = useState([]);
  const [generatingActions, setGeneratingActions] = useState(false);
  const [pillarRating, setPillarRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const successSectionRef = useRef(null);
  const vibeCheckRef = useRef(null);
  const pillarSelectionRef = useRef(null);
  const submitButtonRef = useRef(null);

  useEffect(() => {
    loadQuestionnaire();
  }, []);

  const loadQuestionnaire = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/team-member/weekly-questionnaire?email=${userEmail}&assignment_id=${assignmentId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to load questionnaire');
      }
      
      const data = await response.json();
      setQuestionnaire(data.questionnaire);
      setLoading(false);
    } catch (err) {
      console.error('Error loading questionnaire:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleVibeCheckResponse = async (answer) => {
    if (!isSubmitted) {
      setVibeCheckAnswer(answer);
      
      if (answer === 'YES') {
        setSelectedPillar(''); // Clear pillar selection if they change to YES
        // Scroll to submit button
        setTimeout(() => {
          if (submitButtonRef.current) {
            submitButtonRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100);
      } else {
        setStep(3); // Show pillar selection
        // Scroll to pillar selection
        setTimeout(() => {
          if (pillarSelectionRef.current) {
            pillarSelectionRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
    }
  };

  const handlePillarSelection = (pillar) => {
    setSelectedPillar(pillar);
    // Scroll to submit button
    setTimeout(() => {
      if (submitButtonRef.current) {
        submitButtonRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const generateActionItems = async (pillar) => {
    setGeneratingActions(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/team-member/generate-action-items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            assignmentId: assignmentId,
            pillar_name: pillar
          })
        }
      );
      
      const data = await response.json();
      setActionItems(data.action_items || []);
    } catch (err) {
      console.error('Error generating action items:', err);
      // Provide fallback action items
      setActionItems([
        'Talk to your supervisor about resources and support',
        'Review the GPS principles and best practices',
        'Ask a coworker for tips on improving in this area'
      ]);
    } finally {
      setGeneratingActions(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/team-member/submit-weekly-response`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            assignment_id: assignmentId,
            vibe_check: vibeCheckAnswer,
            pillar_needing_help: vibeCheckAnswer === 'NO' ? selectedPillar : null,
            action_items: null, // Will be generated after submission
            pillar_question_response: pillarRating
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to submit survey');
      }
      
      setSubmitting(false);
      
      // Mark as completed and show success
      setIsSubmitted(true);
      setStep(4);
      
      // Scroll to success section after a brief delay
      setTimeout(() => {
        if (successSectionRef.current) {
          successSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
      
      // Generate action items if they said NO
      if (vibeCheckAnswer === 'NO' && selectedPillar) {
        await generateActionItems(selectedPillar);
      }
      
    } catch (err) {
      console.error('Error submitting survey:', err);
      alert('Failed to submit survey. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
        }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading your survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
        }}>
          <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
            Error Loading Survey
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{error}</p>
          <button onClick={() => navigate('/gps/team')} style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
        }}>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>No questionnaire available</p>
          <button onClick={() => navigate('/gps/team')} style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem 1rem'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827', margin: '0 0 1.5rem 0' }}>
            {questionnaire.pillar_name}
          </h1>
          
          {/* GPS Logo with Words */}
          <div style={{ margin: '1.5rem 0' }}>
            <img 
              src="/gps_words.png" 
              alt="GPS Operational Excellence" 
              style={{ 
                maxWidth: '250px', 
                width: '100%', 
                height: 'auto', 
                display: 'block', 
                margin: '0 auto' 
              }} 
            />
          </div>
          
          <button onClick={() => navigate('/')} style={{
            padding: '0.5rem 1rem',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem'
          }}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>

        {/* Step 1: Theme of the Week */}
        {step >= 1 && (
          <div style={{
            background: '#ffffff',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            border: pillarRating ? '3px solid #10b981' : '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: pillarRating ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: '700'
              }}>
                {pillarRating ? '✓' : '1'}
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', margin: 0 }}>
                Theme of the Week: {questionnaire.pillar_name}
              </h2>
            </div>
            
            {pillarRating && (
              <div style={{
                background: '#d1fae5',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                border: '2px solid #10b981'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '600', margin: 0 }}>
                  ✓ Your rating: <strong>{pillarRating}/5</strong>
                </p>
                <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                  You can change your rating below before submitting
                </p>
              </div>
            )}
            
            <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              {questionnaire.questions[1].text}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', justifyContent: 'space-between' }}>
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => {
                    if (!isSubmitted) {
                      setPillarRating(rating);
                      if (step < 2) setStep(2);
                      // Scroll to vibe check question
                      setTimeout(() => {
                        if (vibeCheckRef.current) {
                          vibeCheckRef.current.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                          });
                        }
                      }, 100);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '1rem 0.5rem',
                    background: pillarRating === rating ? 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)' : '#ffffff',
                    color: pillarRating === rating ? 'white' : '#374151',
                    border: pillarRating === rating ? 'none' : '2px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    cursor: isSubmitted ? 'not-allowed' : 'pointer',
                    opacity: isSubmitted ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {rating}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
              1 (Never) | 2 (Rarely) | 3 (Sometimes) | 4 (Often) | 5 (Always)
            </p>
          </div>
        )}

        {/* Step 2: Vibe Check - Always visible and editable */}
        {step >= 2 && (
          <div ref={vibeCheckRef} style={{
            background: '#ffffff',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            border: vibeCheckAnswer ? '3px solid #10b981' : '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: vibeCheckAnswer ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: '700'
              }}>
                {vibeCheckAnswer ? '✓' : '2'}
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', margin: 0 }}>
                Vibe Check ✨
              </h2>
            </div>
            
            {vibeCheckAnswer && (
              <div style={{
                background: '#d1fae5',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                border: '2px solid #10b981'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '600', margin: 0 }}>
                  ✓ Your answer: <strong>{vibeCheckAnswer}</strong>
                </p>
                <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                  You can change your answer below before submitting
                </p>
              </div>
            )}
            
            <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Do you have everything you need to be successful?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <button
                onClick={() => handleVibeCheckResponse('YES')}
                style={{
                  padding: '2rem 1rem',
                  background: vibeCheckAnswer === 'YES' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#ffffff',
                  color: vibeCheckAnswer === 'YES' ? 'white' : '#374151',
                  border: vibeCheckAnswer === 'YES' ? 'none' : '2px solid #d1d5db',
                  borderRadius: '0.75rem',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  cursor: isSubmitted ? 'not-allowed' : 'pointer',
                  opacity: isSubmitted ? 0.7 : (vibeCheckAnswer && vibeCheckAnswer !== 'YES' ? 0.7 : 1),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '3rem' }}>👍</div>
                Yes
              </button>
              <button
                onClick={() => handleVibeCheckResponse('NO')}
                style={{
                  padding: '2rem 1rem',
                  background: vibeCheckAnswer === 'NO' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#ffffff',
                  color: vibeCheckAnswer === 'NO' ? 'white' : '#374151',
                  border: vibeCheckAnswer === 'NO' ? 'none' : '2px solid #d1d5db',
                  borderRadius: '0.75rem',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  cursor: isSubmitted ? 'not-allowed' : 'pointer',
                  opacity: isSubmitted ? 0.7 : (vibeCheckAnswer && vibeCheckAnswer !== 'NO' ? 0.7 : 1),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '3rem' }}>👎</div>
                No
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pillar Selection (if NO) - Always visible once NO is selected */}
        {step >= 3 && vibeCheckAnswer === 'NO' && (
          <div ref={pillarSelectionRef} style={{
            background: '#ffffff',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            border: selectedPillar ? '3px solid #10b981' : '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: selectedPillar ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: '700'
              }}>
                {selectedPillar ? '✓' : '3'}
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827', margin: 0 }}>
                Where do you need the most help?
              </h3>
            </div>
            
            {selectedPillar && (
              <div style={{
                background: '#d1fae5',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                border: '2px solid #10b981'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '600', margin: 0 }}>
                  ✓ You selected: <strong>{selectedPillar}</strong>
                </p>
                <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                  You can change your selection below before submitting
                </p>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {[
                'We Own Safety',
                'We Own Quality',
                'We Own the Workplace',
                'We Develop People',
                'We Drive Flow',
                'We Drive Value',
                'We Seek Perfection'
              ].map((pillar) => (
                <button
                  key={pillar}
                  onClick={() => {
                    if (!isSubmitted) {
                      handlePillarSelection(pillar);
                    }
                  }}
                  style={{
                    padding: '1rem',
                    background: selectedPillar === pillar ? 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)' : '#ffffff',
                    color: selectedPillar === pillar ? 'white' : '#374151',
                    border: selectedPillar === pillar ? 'none' : '2px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: isSubmitted ? 'not-allowed' : 'pointer',
                    opacity: isSubmitted ? 0.7 : 1,
                    transition: 'all 0.2s',
                    textAlign: 'center'
                  }}
                >
                  {pillar}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Success Screen */}
        {step === 4 && (
          <div 
            ref={successSectionRef}
            style={{
              background: '#ffffff',
              borderRadius: '0.75rem',
              padding: '2rem',
              marginBottom: '1.5rem',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              textAlign: 'center'
            }}>
            {vibeCheckAnswer === 'NO' && selectedPillar && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                border: '2px solid #f59e0b',
                textAlign: 'left'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e', marginBottom: '0.5rem', textAlign: 'center' }}>
                  💡 Action items for:
                </h3>
                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#92400e', marginBottom: '1rem', textAlign: 'center' }}>
                  {selectedPillar}
                </h4>
                <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '1rem', textAlign: 'center' }}>
                  Here are 3 things you can do this week:
                </p>
                
                {generatingActions ? (
                  <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div className="spinner" style={{ margin: '0 auto' }}></div>
                    <p style={{ color: '#92400e', marginTop: '1rem', fontSize: '0.875rem' }}>
                      Generating personalized action items...
                    </p>
                  </div>
                ) : (
                  <ol style={{ paddingLeft: '1.5rem', margin: 0 }}>
                    {actionItems.map((item, idx) => (
                      <li key={idx} style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                        {item}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}

            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={48} color="white" strokeWidth={3} />
            </div>
            
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
              Thank You!
            </h2>
            
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: '1.6' }}>
              Your feedback has been submitted successfully. Your input helps us improve every day.
            </p>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.875rem 2rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {/* Submit Button - Show when ready and not submitted */}
        {step >= 2 && step < 4 && !isSubmitted && vibeCheckAnswer && pillarRating && (vibeCheckAnswer === 'YES' || selectedPillar) && (
          <button
            ref={submitButtonRef}
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '1rem 1.5rem',
              background: submitting ? '#d1d5db' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Send size={20} />
            {submitting ? 'Submitting...' : 'Submit Survey'}
          </button>
        )}

        {/* Show submitted message if already submitted */}
        {isSubmitted && step < 4 && (
          <div style={{
            width: '100%',
            padding: '1rem 1.5rem',
            background: '#d1fae5',
            color: '#065f46',
            border: '2px solid #10b981',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            textAlign: 'center',
            marginTop: '1rem'
          }}>
            ✓ Survey submitted! Your responses are locked.
          </div>
        )}
      </div>
    </div>
  );
};

export default GPSVibeCheckSurvey;
